import { useMemo } from "react"
import { useDroppable } from "@dnd-kit/core"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { CalendarPieceCard } from "./CalendarPieceCard"
import { CalendarBlockChip, blockOverlapsDay, blockVisible } from "./CalendarBlockChip"
import type { CalendarSlot, CalendarChannel, CalendarHoliday, CalendarBlock } from "./types"

type Props = {
  anchor: string
  slots: CalendarSlot[]
  channels: CalendarChannel[]
  holidays: CalendarHoliday[]
  blocks: CalendarBlock[]
  visibleChannels: Set<string>
  // Step the calendar by whole months (delta -1 = previous, +1 = next).
  onNavigateMonth: (delta: number) => void
}

const DAY_HEADERS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]

export function CalendarMonthView({ anchor, slots, channels, holidays, blocks, visibleChannels, onNavigateMonth }: Props) {
  const channelBySlug = useMemo(() => {
    const map = new Map<string, CalendarChannel>()
    channels.forEach((c) => map.set(c.slug, c))
    return map
  }, [channels])

  const monthStart = useMemo(() => {
    const d = new Date(`${anchor}T00:00:00`)
    return new Date(d.getFullYear(), d.getMonth(), 1)
  }, [anchor])

  // Six-week grid starting on the Sunday on/before the 1st.
  const cells = useMemo(() => {
    const start = new Date(monthStart)
    start.setDate(start.getDate() - start.getDay())
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(start)
      d.setDate(start.getDate() + i)
      return d
    })
  }, [monthStart])

  const today = new Date()

  const prevMonthLabel = useMemo(() => monthLabel(monthStart, -1), [monthStart])
  const nextMonthLabel = useMemo(() => monthLabel(monthStart, 1), [monthStart])

  return (
    // No overflow-hidden here (the week view matches): the piece cards'
    // hover overlays must be free to extend past the grid's edges.
    <div className="rounded-md border border-hairline bg-surface">
      <div className="grid grid-cols-7 border-b border-hairline">
        {DAY_HEADERS.map((d) => (
          <div key={d} className="px-2 py-1.5 text-center text-xs text-ink-muted">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((d) => {
          const iso = d.toISOString().slice(0, 10)
          const inMonth = d.getMonth() === monthStart.getMonth()
          const isToday = sameLocalDay(d, today)
          const holiday = holidays.find((h) => h.date === iso)

          const cellSlots = slots.filter((s) => {
            if (!visibleChannels.has(s.channel)) return false
            const at = new Date(s.publish_at)
            return sameLocalDay(at, d)
          })

          const uploadGhosts = slots.filter((s) => {
            if (!s.use_platform_scheduling || !s.upload_at) return false
            if (!visibleChannels.has(s.channel)) return false
            const upload = new Date(s.upload_at)
            return sameLocalDay(upload, d)
          })

          const cellBlocks = blocks.filter(
            (b) => blockVisible(b, visibleChannels) && blockOverlapsDay(b, d)
          )

          return (
            <MonthDayCell
              key={iso}
              date={d}
              iso={iso}
              inMonth={inMonth}
              isToday={isToday}
              holiday={holiday}
              cellSlots={cellSlots}
              uploadGhosts={uploadGhosts}
              cellBlocks={cellBlocks}
              channels={channels}
              channelBySlug={channelBySlug}
            />
          )
        })}
      </div>
      <div className="flex items-center justify-between border-t border-hairline px-2 py-2">
        <Button variant="secondary" onClick={() => onNavigateMonth(-1)} aria-label={`Go to ${prevMonthLabel}`}>
          <ChevronLeft className="h-4 w-4" />
          {prevMonthLabel}
        </Button>
        <Button variant="secondary" onClick={() => onNavigateMonth(1)} aria-label={`Go to ${nextMonthLabel}`}>
          {nextMonthLabel}
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}

// Human label for the month `delta` months away from `monthStart`,
// e.g. "May 2026" / "July 2026".
function monthLabel(monthStart: Date, delta: number) {
  const d = new Date(monthStart.getFullYear(), monthStart.getMonth() + delta, 1)
  return d.toLocaleDateString(undefined, { month: "long", year: "numeric" })
}

function MonthDayCell({
  date,
  iso,
  inMonth,
  isToday,
  holiday,
  cellSlots,
  uploadGhosts,
  cellBlocks,
  channels,
  channelBySlug
}: {
  date: Date
  iso: string
  inMonth: boolean
  isToday: boolean
  holiday: CalendarHoliday | undefined
  cellSlots: CalendarSlot[]
  uploadGhosts: CalendarSlot[]
  cellBlocks: CalendarBlock[]
  channels: CalendarChannel[]
  channelBySlug: Map<string, CalendarChannel>
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `month-cell-${iso}`, data: { date, hour: 9 } })
  const isPast = isPastDay(date)

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "min-h-[120px] min-w-0 border-r border-t border-hairline p-1.5",
        // Today and future days read as white; past days keep the grid's
        // gray surface (and stay dimmed). Adjacent-month dates stay muted.
        !isPast && !isOver && "bg-page",
        !inMonth && "text-ink-muted",
        isPast && "opacity-50",
        isOver && "bg-accent-faded"
      )}
    >
      <div className="flex items-baseline justify-between">
        <span
          className={cn(
            "text-xs font-medium",
            // Mark today with a filled pill so it stays identifiable on the
            // new white background.
            isToday
              ? "inline-flex h-5 w-5 items-center justify-center rounded-full bg-accent text-page"
              : !inMonth && "text-ink-muted"
          )}
        >
          {date.getDate()}
        </span>
        {holiday && (
          <span className="truncate text-[10px] text-ink-muted" title={holiday.name}>
            {holiday.name}
          </span>
        )}
      </div>
      <div className="mt-1 flex flex-col gap-1">
        {cellBlocks.map((b) => (
          <CalendarBlockChip key={`block-${b.id}`} block={b} channels={channels} compact />
        ))}
        {(() => {
          // Merge publish + upload entries and sort chronologically so a
          // piece's upload ghost renders before its later publish card.
          const entries = [
            ...cellSlots.map((s) => ({ slot: s, variant: "publish" as const, t: new Date(s.publish_at).getTime() })),
            ...uploadGhosts.map((s) => ({ slot: s, variant: "upload" as const, t: new Date(s.upload_at!).getTime() })),
          ].sort((a, b) => a.t - b.t)

          // Render every entry — no "+N more" truncation. The CSS grid row
          // stretches to the tallest cell, so a busy day just makes its
          // whole week taller.
          return entries.map(({ slot: s, variant }) => {
            const ch = channelBySlug.get(s.channel)
            if (!ch) return null
            return (
              <CalendarPieceCard
                key={variant === "upload" ? `upload-${s.id}` : s.id}
                slot={s}
                channel={ch}
                variant={variant}
                layout="icon"
              />
            )
          })
        })()}
      </div>
    </div>
  )
}

function sameLocalDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

// A day is "past" when its calendar date falls before today's. Today and
// future days stay at full opacity; earlier days are dimmed in the grid.
function isPastDay(date: Date) {
  const now = new Date()
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
  return startOfDate < startOfToday
}
